import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'
import {
  ComplianceExtraFieldsConfigsFieldFragment,
  SaleInvoiceUpsertSidepanelQuery,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { ComplianceExtraFieldSelector } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/ComplianceExtraFieldSelector'
import {
  FieldComplianceExtraField,
  FormComplianceExtraFields,
  LineItemWithComplianceExtraFieldsFormFields,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/types'
import { LineItem } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/SaleInvoiceForm/types'
import {
  buildComplianceExtraFieldsWithDefaultValues,
  sortedComplianceExtraFieldsForContext,
} from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'

import LineItemFields from './LineItemFields'
import { getUpdatedComplianceExtraFields } from './utils/complianceExtraFields'
import { validateLineItem } from './utils/lineItemValidation'

type FinanceComplianceExtraFieldConfig = ComplianceExtraFieldsConfigsFieldFragment
type FinanceFinancialDocumentComplianceExtraFieldsConfig = NonNullable<
  SaleInvoiceUpsertSidepanelQuery['finance']['financialDocumentComplianceExtraFieldsConfigs']
>[number]

interface LineItemModalProps<
  T extends {
    lineItems: LineItem[]
    complianceExtraFields?: FormComplianceExtraFields | null
    currency?: string
  }
> {
  form: FormikProps<T>
  isCreditNote: boolean
  lineItem: LineItem
  currency?: string
  country?: string
  onClose: () => void
  complianceExtraFieldsConfig?: FinanceFinancialDocumentComplianceExtraFieldsConfig
  complianceExtraFieldErrors: Record<string, string> | undefined
}

const LineItemModal = <
  T extends {
    lineItems: LineItem[]
    complianceExtraFields?: FormComplianceExtraFields | null
    currency?: string
  }
>({
  form,
  isCreditNote,
  lineItem,
  currency,
  country,
  onClose,
  complianceExtraFieldsConfig,
  complianceExtraFieldErrors,
}: LineItemModalProps<T>): React.ReactElement => {
  const hasComplianceExtraFields = !!complianceExtraFieldsConfig?.fields?.length

  const buildFieldComplianceExtraFields = (): FieldComplianceExtraField[] => {
    if (!complianceExtraFieldsConfig) {
      return []
    }
    const existingFields = form.values.complianceExtraFields?.fields.filter(
      (field) => field.associatedFieldId === lineItem.id
    )
    if (existingFields && existingFields.length > 0) {
      return existingFields
    }
    return buildComplianceExtraFieldsWithDefaultValues(
      complianceExtraFieldsConfig,
      'line_item',
      isCreditNote,
      String(lineItem.id)
    )
  }

  //check if lineItem exists in form.values.lineItems or its a new line item
  const lineItemIndex = useUnsafeMemo(
    () => form.values.lineItems?.findIndex((item: LineItem) => item.id === lineItem.id),
    [form.values.lineItems, lineItem.id]
  )
  const modalTitle = useUnsafeMemo(
    () =>
      lineItemIndex !== -1
        ? i18n.t('finance.sale_invoices.form.line_items.edit_line_item')
        : i18n.t('finance.sale_invoices.form.line_items.title'),
    [lineItemIndex]
  )

  const lineItemForm = useForm<LineItemWithComplianceExtraFieldsFormFields>({
    enableReinitialize: true,
    validateOnChange: false,
    defaultValues: {
      lineItem: lineItem,
      complianceExtraFields: buildFieldComplianceExtraFields(),
    },
    validate: validateLineItem,
    onSubmit: async (values) => {
      if (lineItemIndex !== -1) {
        form.setFieldValue(`lineItems.${lineItemIndex}`, values.lineItem)
      } else {
        form.setFieldValue('lineItems', [...form.values.lineItems, values.lineItem])
      }
      //save complianceExtraFields in main form
      if (hasComplianceExtraFields) {
        const updatedComplianceExtraFields = getUpdatedComplianceExtraFields(
          form.values.complianceExtraFields?.fields || [],
          values.complianceExtraFields,
          String(lineItem.id),
          complianceExtraFieldsConfig
        )
        form.setFieldValue('complianceExtraFields', {
          ...form.values.complianceExtraFields,
          fields: updatedComplianceExtraFields,
        })
      }
      onClose()
      return { success: true, data: {}, errors: [] }
    },
  })

  const lineItemComplianceExtraFields = useUnsafeMemo(() => {
    if (!hasComplianceExtraFields) {
      return []
    }
    return sortedComplianceExtraFieldsForContext(complianceExtraFieldsConfig.fields, 'line_item')
  }, [complianceExtraFieldsConfig])

  const handleOnChangeComplianceExtraField = (
    complianceExtraFieldConfig: FinanceComplianceExtraFieldConfig,
    value: string
  ) => {
    const complianceExtraFieldIndex = lineItemForm.values.complianceExtraFields.findIndex(
      (item) =>
        item.associatedFieldId === lineItem.id && item.name === complianceExtraFieldConfig.name
    )
    if (complianceExtraFieldIndex !== -1) {
      lineItemForm.setFieldValue(`complianceExtraFields.${complianceExtraFieldIndex}.value`, value)
    } else {
      lineItemForm.setFieldValue('complianceExtraFields', [
        ...lineItemForm.values.complianceExtraFields,
        {
          name: complianceExtraFieldConfig.name,
          value: value,
          associatedFieldId: lineItem.id,
        },
      ])
    }
  }

  return (
    <Modal
      illustration="document-edit"
      title={modalTitle}
      onClose={onClose}
      onSubmit={() => lineItemForm.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('finance.buttons.cancel')}
          </Button>
          <Button submit disabled={!lineItemForm.dirty}>
            {i18n.t('common.save')}
          </Button>
        </Box>
      }
    >
      <Box gap="s8" paddingTop="s8" width="s400" flexDirection="column">
        <LineItemFields
          lineItem={lineItemForm.values.lineItem}
          errors={lineItemForm.errors.lineItem}
          onChange={lineItemForm.setFieldValue}
          currency={currency}
          country={country}
        />
        {lineItemComplianceExtraFields.map((complianceExtraField, index) => (
          <ComplianceExtraFieldSelector
            isCreditNote={isCreditNote}
            key={index}
            complianceExtraFieldsConfigsField={complianceExtraField}
            complianceExtraFields={lineItemForm.values.complianceExtraFields}
            onChange={(value) => handleOnChangeComplianceExtraField(complianceExtraField, value)}
            mandatory={
              isCreditNote
                ? complianceExtraField.mandatoryForCreditNote
                : complianceExtraField.mandatoryForStandardInvoice
            }
            error={complianceExtraFieldErrors?.[complianceExtraField.name]}
          />
        ))}
      </Box>
    </Modal>
  )
}

export default LineItemModal
